import * as fromLookups from './actions';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IAuthorizationType } from '../IAuthorizationType';
import { ITask } from '../ITask';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { IDirectReport } from '../IDirectReport';
import { IServiceItem } from '../IServiceItem';
import { IUserGroup } from '../IUserGroup';
import { IAuthorizationClass } from '../IAuthorizationClass';
import { PayrollItem } from '../../payroll/item/store/payroll-item.model';
import { IPerDiemLocale } from '../IPerDiemLocal';
import { ITravelType } from '../ITravelType';
import { IExpenseItem } from '../IExpenseItem';
import { ILocale } from '../ILocale';
import { IState } from '../IState';
import { IContractType } from '../IContractType';
import { ILocation } from '../ILocation';
import { IndirectType } from '../IndirectType';
import { IEntityFilter } from '../IEntityFilter';
import { ISplitDay } from '../ISplitDay';
import { stagger } from '@angular/animations';
import { IUser } from '../../user-info';
import { IUserIdFormat } from '../iuser-id-format';
import { IDayOfWeek } from '../IDayOfWeek';
import { ITimeZone } from '../ITimeZone';
import { Color } from '../color.model';
import { MobileProvider } from '../mobile-provider.model';
import { MessageDeliveryType } from '../message-delivery-type.model';
import { Account } from '../../account/store/account.model';

const log = new LoggingService();

export interface LookupState {
    authorizationTypes: Array<IAuthorizationType>;
    directReports: Array<IDirectReport>;
    tasks: Array<ITask>;
    userTasks: Array<ITask>;
    contractTasks: Array<ITask>;
    serviceItems: Array<IServiceItem>;
    userGroups: Array<IUserGroup>;
    authClasses: Array<IAuthorizationClass>;
    payrollItems: Array<PayrollItem>;
    perDiemTypes: Array<IPerDiemLocale>;
    locales: Array<ILocale>;
    travelTypes: Array<ITravelType>;
    expenseItems: Array<IExpenseItem>;
    contractTypes: Array<IContractType>;
    indirectTypes: Array<IndirectType>;
    states: Array<IState>;
    countries: Array<ILocation>;
    areas: Array<ILocation>;
    locations: Array<ILocation>;
    entityFilters: Array<IEntityFilter>;
    splitDays: Array<ISplitDay>;
    payrollFrequency: Array<{ Payrollfrequency: number }>;
    userIdFormats: Array<IUserIdFormat>;
    daysOfWeek: Array<IDayOfWeek>;
    timeZones: Array<ITimeZone>;
    userTimeZones: Array<ITimeZone>;
    colors: Array<Color>;
    mobileProviders: MobileProvider[];
    creditCards: Array<Account>;
    messageDeliveryTypes: MessageDeliveryType[];
}

export const initialState: LookupState = {
    authorizationTypes: [],
    directReports: [],
    tasks: [],
    userTasks: [],
    contractTasks: [],
    serviceItems: [],
    userGroups: [],
    authClasses: [],
    payrollItems: [],
    perDiemTypes: [],
    locales: [],
    travelTypes: [],
    expenseItems: [],
    contractTypes: [],
    indirectTypes: [],
    states: [],
    countries: [],
    areas: [],
    locations: [],
    entityFilters: [],
    splitDays: [],
    payrollFrequency: [],
    userIdFormats: [],
    daysOfWeek: [],
    timeZones: [],
    userTimeZones: [],
    colors: [],
    mobileProviders: [],
    creditCards: [],
    messageDeliveryTypes: []
};

export function reducer(
    state: LookupState = initialState,
    action: fromLookups.LookupActionsUnion
): LookupState {
    switch (action.type) {
        case fromLookups.LookupActionTypes.SetAuthorizationTypes:
            return {
                ...state,
                authorizationTypes: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetTasks:
            return {
                ...state,
                tasks: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetUserTasks:
            return {
                ...state,
                userTasks: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetContractTasks:
            return {
                ...state,
                contractTasks: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetDirectReports:
            return {
                ...state,
                directReports: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetServiceItems:
            return {
                ...state,
                serviceItems: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetUserGroups:
            return {
                ...state,
                userGroups: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetAuthorizationClasses:
            return {
                ...state,
                authClasses: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetPayrollItems:
            return {
                ...state,
                payrollItems: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetPerDiemTypes:
            return {
                ...state,
                perDiemTypes: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetLocales:
            return {
                ...state,
                locales: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetTravelTypes:
            return {
                ...state,
                travelTypes: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetExpenseItems:
            return {
                ...state,
                expenseItems: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetContractTypes:
            return {
                ...state,
                contractTypes: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetIndirectTypes:
            return {
                ...state,
                indirectTypes: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetStates:
            return {
                ...state,
                states: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetCountries:
            return {
                ...state,
                countries: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetAreas:
            return {
                ...state,
                areas: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetLocations:
            return {
                ...state,
                locations: [...(action.payload || [])]
            };
        case fromLookups.LookupActionTypes.SetEntityFilter:
            return {
                ...state,
                entityFilters: [
                    ...(state.entityFilters || []).filter(
                        x =>
                            !(action.filterTypes || []).some(
                                y => y.FilterID === x.FilterID
                            )
                    ),
                    ...(action.filterTypes || [])
                ]
            };
        case fromLookups.LookupActionTypes.SetSplitday:
            return {
                ...state,
                splitDays: action.payload
            };
        case fromLookups.LookupActionTypes.SetPayrollFrequency:
            return {
                ...state,
                payrollFrequency: action.payload
            };
        case fromLookups.LookupActionTypes.SetUserIdFormats:
            return {
                ...state,
                userIdFormats: action.payload
            };
        case fromLookups.LookupActionTypes.SetDaysOfWeek:
            return {
                ...state,
                daysOfWeek: action.payload
            };
        case fromLookups.LookupActionTypes.SetTimeZones:
            return {
                ...state,
                timeZones: action.payload
            };
        case fromLookups.LookupActionTypes.SetUserTimeZones:
            return {
                ...state,
                userTimeZones: action.payload
            };
        case fromLookups.LookupActionTypes.RESET:
            return {
                ...initialState
            };
        case fromLookups.LookupActionTypes.SetColors:
            return {
                ...state,
                colors: action.payload
            };
        case fromLookups.LookupActionTypes.SetMobileProviders:
            return {
                ...state,
                mobileProviders: action.payload
            };
        case fromLookups.LookupActionTypes.SetCreditCards:
            return {
                ...state,
                creditCards: action.payload
            };
        case fromLookups.LookupActionTypes.SetMessageDeliveryTypes:
            return {
                ...state,
                messageDeliveryTypes: action.payload
            };
        default:
            return state;
    }
}

const lookupState = createFeatureSelector<LookupState>('lookups');

export const getIndirectTypes = createSelector(
    lookupState,
    x => x.indirectTypes
);
export const getContractTypes = createSelector(
    lookupState,
    x => x.contractTypes
);
export const getAuthorizationTypesLookup = createSelector(
    lookupState,
    x => x.authorizationTypes
);
export const getTasksLookup = createSelector(lookupState, x => x.tasks);
export const getUserTasks = createSelector(lookupState, x => x.userTasks);
export const getContractTasks = createSelector(lookupState, x => x.contractTasks);
export const getDirectReportsLookup = createSelector(
    lookupState,
    x => x.directReports
);
export const getActiveDirectReportsLookup = createSelector(lookupState, x =>
    x.directReports.filter(y => y.IsActive === true)
);
export const getServiceItemsLookup = createSelector(
    lookupState,
    x => x.serviceItems
);
export const getOtherChargeItemsLookup = createSelector(
    getServiceItemsLookup,
    x => x.filter(item => item.ServiceItemType === 'OtherChargeItem')
);
export const getUserGroupsLookup = createSelector(
    lookupState,
    x => x.userGroups
);
export const getAuthClassesLookup = createSelector(
    lookupState,
    x => x.authClasses
);
export const getPayrollItemsLookup = createSelector(
    lookupState,
    x => x.payrollItems
);
export const getLocalesLookup = createSelector(lookupState, x => x.locales);
export const getPerDiemTypesLookup = createSelector(
    lookupState,
    x => x.perDiemTypes
);
export const getTravelTypesLookup = createSelector(
    lookupState,
    x => x.travelTypes
);
export const getExpenseItems = createSelector(lookupState, x => x.expenseItems);
export const getStates = createSelector(lookupState, x => x.states);
export const getCountries = createSelector(lookupState, x => x.countries);
export const getAreas = createSelector(lookupState, x => x.areas);
export const getLocations = createSelector(lookupState, x => x.locations);
export const getCostCenters = createSelector(lookupState, x =>
    x.userGroups.filter(y => y.PrefixedGroupID[0].toLocaleUpperCase() === 'C')
);
export const getEntityFilters = createSelector(
    lookupState,
    (x: LookupState, p): IEntityFilter[] =>
        x.entityFilters.filter(f => f.FilterEntity === p.EntityType)
);
export const getSplitdays = createSelector(lookupState, x => x.splitDays);
export const getPayrollFrequency = createSelector(
    lookupState,
    x => x.payrollFrequency
);
export const getUserIdFormats = createSelector(
    lookupState,
    x => x.userIdFormats
);
export const getDaysOfWeek = createSelector(lookupState, x => x.daysOfWeek);
export const getTimeZones = createSelector(lookupState, x => x.timeZones);
export const getUserTimeZones = createSelector(
    lookupState,
    x => x.userTimeZones
);
export const getColors = createSelector(lookupState, x => x.colors);
export const selectMobileProviders = createSelector(
    lookupState,
    x => x.mobileProviders
);
export const getManageUsers = createSelector(lookupState, x => x);
export const getCreditCards = createSelector(lookupState, x => x.creditCards);
export const selectMessageDeliveryTypes = createSelector(
    lookupState,
    x => x.messageDeliveryTypes
);
